import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function NotesThread({ clientId, mode }) {
  const isAdmin = mode === "admin";
  const listUrl = isAdmin ? `/portal/clients/${clientId}/notes` : "/portal/family/notes";
  const readUrl = isAdmin ? `/portal/clients/${clientId}/notes/read` : "/portal/family/notes/read";

  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [shared, setShared] = useState(true);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewNote, setViewNote] = useState(null);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(listUrl);
      setNotes(r.data);
      api.post(readUrl).catch(() => {});
    } catch {
      /* keep quiet — thread is a convenience */
    }
    setLoading(false);
  };
  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [notes.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(listUrl, isAdmin ? { body, shared } : { body });
      setText("");
      await load();
    } catch {
      toast.error("Could not send the note.");
    }
    setSending(false);
  };

  const mine = (n) => (isAdmin ? n.author_role === "admin" : n.author_role === "family");

  return (
    <div data-testid="notes-thread">
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-sage" /></div>
      ) : notes.length === 0 ? (
        <div className="text-center py-6">
          <MessageCircle className="h-8 w-8 text-sage/40 mx-auto mb-2" />
          <p className="text-sm text-charcoal/40">
            No notes yet.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
          {notes.map((n) => (
            <div key={n.id} className={`flex ${mine(n) ? "justify-end" : "justify-start"}`}>
              <button
                onClick={() => setViewNote(n)}
                data-testid={`note-${n.id}`}
                title="Tap to view details"
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-left transition-transform hover:-translate-y-0.5 ${mine(n) ? "bg-sage text-white rounded-br-md" : "bg-white border border-charcoal/10 text-charcoal rounded-bl-md"}`}
              >
                <p className="text-sm whitespace-pre-wrap">{n.body}</p>
                <p className={`text-[10px] mt-1 ${mine(n) ? "text-white/70" : "text-charcoal/40"}`}>
                  {n.author_name} · {format(new Date(n.created_at), "MMM d, h:mm a")}
                </p>
              </button>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        {isAdmin && (
          <label className="flex items-center gap-1.5 text-xs text-charcoal-soft cursor-pointer select-none shrink-0 pl-1" data-testid="note-share-toggle" title="When on, the family can see this note in their portal">
            <input
              type="checkbox"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
              className="h-4 w-4 accent-sage"
            />
            Share
          </label>
        )}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="note-input"
          placeholder={isAdmin ? "Add a note…" : "Write a note to Nicole…"}
          className="flex-1 h-11 rounded-full border border-charcoal/15 bg-white px-4 text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:ring-2 focus:ring-sage/40"
        />
        <button
          onClick={send}
          disabled={sending || !text.trim()}
          data-testid="note-send-button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-sage text-white hover:bg-sage-dark transition-colors disabled:opacity-50 shrink-0"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={!!viewNote} onOpenChange={() => setViewNote(null)}>
        <DialogContent className="bg-sand max-w-sm" data-testid="note-detail-dialog">
          {viewNote && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Note details</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-charcoal-soft">From</span>
                  <span className="text-charcoal font-medium">{viewNote.author_name}{viewNote.author_role === "admin" ? " (Nicole)" : " (Family)"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-charcoal-soft">Date</span>
                  <span className="text-charcoal" data-testid="note-detail-date">{format(new Date(viewNote.created_at), "EEEE, MMMM d, yyyy")}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-charcoal-soft">Time</span>
                  <span className="text-charcoal" data-testid="note-detail-time">{format(new Date(viewNote.created_at), "h:mm a")}</span>
                </div>
                <div className="bg-white rounded-xl border border-charcoal/10 p-4 text-charcoal whitespace-pre-wrap" data-testid="note-detail-body">
                  {viewNote.body}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
